import { useCallback } from 'react';
import { redirect } from 'react-router-dom';
import { useAppSelector } from '.';
import { ACTIVE_TAB_PARAM, DEFAULT_ANALYTICS_AVERAGE_BY, DEFAULT_ANALYTICS_DATE_RANGE } from '../constants';
import { AnalyticsFilterKeys, AnalyticsTab, Permissions } from '../types';
import { getDateRangeParam } from '../utils';

/**
 * Returns a Router loader for the Analytics page.
 * Ensures the default filters are applied to the Analytics page on initial load.
 */
const useAnalyticsLoader = () => {
  const user = useAppSelector((state) => state.auth.user);
  const permissions = user?.permissions || [];
  const canViewTeamPerformance = permissions.includes(Permissions.VIEW_TEAM_ANALYTICS);

  const analyticsLoader = useCallback(
    ({ request }: { request: Request }) => {
      const url = new URL(request.url);
      const activeTab = url.searchParams.get(ACTIVE_TAB_PARAM);
      const isProgressReportTab = activeTab === AnalyticsTab.PROGRESS_REPORT;

      const validTabs = Object.values(AnalyticsTab).filter(
        (tab) => tab !== AnalyticsTab.TEAM_PERFORMANCE || canViewTeamPerformance
      );
      const hasTab = !!activeTab && validTabs.includes(activeTab as AnalyticsTab);
      const hasDateRange = url.searchParams.has(AnalyticsFilterKeys.DATE_RANGE);
      const hasAverageBy = url.searchParams.has(AnalyticsFilterKeys.AVERAGE_BY);

      if (hasTab && hasDateRange && (!isProgressReportTab || hasAverageBy)) return null;

      // If no tab is selected, set the default tab.
      if (!hasTab) {
        url.searchParams.set(ACTIVE_TAB_PARAM, AnalyticsTab.ACTIVITY);
      }

      // If no date range is selected, set the default date range, parsing the date range into the correct format.
      if (!hasDateRange) {
        url.searchParams.set(AnalyticsFilterKeys.DATE_RANGE, getDateRangeParam(DEFAULT_ANALYTICS_DATE_RANGE));
      }

      // If the progress report tab is selected and no average by is selected, set the default average by.
      if (isProgressReportTab && !hasAverageBy) {
        url.searchParams.set(AnalyticsFilterKeys.AVERAGE_BY, DEFAULT_ANALYTICS_AVERAGE_BY);
      }

      return redirect(url.pathname + url.search);
    },
    [canViewTeamPerformance]
  );

  return analyticsLoader;
};

export default useAnalyticsLoader;
